<ng-container *ngIf="appointment">
  <ng-container *ngIf="!workflow; else workflowTemplate">
    <button
      *appHasPermission="'appointment.edit'"
      mat-icon-button
      [matMenuTriggerFor]="eligibilityMenu"
      [matTooltip]="tooltipText$ | async | translate"
      matTooltipPosition="above"
      (click)="$event.stopPropagation()"
    >
      <div
        fxLayout="row"
        fxLayoutAlign="center center"
        *ngIf="inProgress">
        <mat-progress-spinner color="primary" mode="indeterminate" diameter="25"></mat-progress-spinner>
      </div>
      <div class="icon-container">
        <!-- Status icon for primary insurance-->
        <mat-icon
          *ngIf="!inProgress"
          [ngClass]="[
        eligibilityStatusColor(appointment.eligibilityPrimaryStatus),
        appointment.eligibilitySecondaryStatus ? 'primary-icon' : '',
      ]">
          {{ eligibilityStatusIcon(appointment.eligibilityPrimaryStatus) }}
        </mat-icon>
        <!-- Status icon for secondary insurance-->
        <mat-icon
          class="secondary-icon"
          *ngIf="appointment.eligibilityDetails?.secondary?.status && !inProgress"
          [ngClass]="eligibilityStatusColor(appointment.eligibilitySecondaryStatus)">
          {{ eligibilityStatusIcon(appointment.eligibilitySecondaryStatus) }}
        </mat-icon>
      </div>
    </button>
  </ng-container>

  <mat-menu #eligibilityMenu="matMenu">
    <button
      mat-menu-item
      *ngIf="appointment.eligibilityPrimaryId && !workflow"
      (click)="goto(appointment.url)"
      [queryParams]="{ type: 'eligibility' }"
    >
      <mat-icon>launch</mat-icon>
      <span>{{ 'eligibility.icon.view' | translate }}</span>
    </button>
    <div
      [matTooltip]="'appointment.eligibility-status-text.manual-verification-unavailable' | translate"
      [matTooltipDisabled]="canMarkManually(PATIENT_INSURANCE_TYPE.PRIMARY) || isActive(appointment.eligibilityPrimaryStatus)"
      matTooltipPosition="above"
    >
      <button
        mat-menu-item
        [disabled]="isActive(appointment.eligibilityPrimaryStatus) || !canMarkManually(PATIENT_INSURANCE_TYPE.PRIMARY)"
        (click)="markInsuranceAs('primary', 'active')"
      >
        <mat-icon class="green">verified_user</mat-icon>
        <span>{{ 'eligibility.icon.primary-active' | translate }}</span>
      </button>
    </div>
    <div
      [matTooltip]="'appointment.eligibility-status-text.manual-verification-unavailable' | translate"
      [matTooltipDisabled]="canMarkManually(PATIENT_INSURANCE_TYPE.PRIMARY) || appointment.eligibilityPrimaryStatus === ELIGIBILITY_STATUS.INACTIVE"
      matTooltipPosition="above"
    >
      <button
        mat-menu-item
        [disabled]="appointment.eligibilityPrimaryStatus === ELIGIBILITY_STATUS.INACTIVE || !canMarkManually(PATIENT_INSURANCE_TYPE.PRIMARY)"
        (click)="markInsuranceAs('primary', 'inactive')"
      >
        <mat-icon class="yellow">remove_moderator</mat-icon>
        <span>{{ 'eligibility.icon.primary-inactive' | translate }}</span>
      </button>
    </div>
    <button
      mat-menu-item
      *ngIf="!workflow"
      [matMenuTriggerFor]="secondaryInsuranceMenu"
    >
      <mat-icon>shield</mat-icon>
      {{ 'common.secondary' | translate }}
    </button>
  </mat-menu>

  <mat-menu #secondaryInsuranceMenu="matMenu">
    <div
      [matTooltip]="'appointment.eligibility-status-text.manual-verification-unavailable' | translate"
      [matTooltipDisabled]="canMarkManually(PATIENT_INSURANCE_TYPE.SECONDARY) || isActive(appointment.eligibilitySecondaryStatus)"
      matTooltipPosition="above"
    >
      <button
        mat-menu-item
        [disabled]="isActive(appointment.eligibilitySecondaryStatus) || !canMarkManually(PATIENT_INSURANCE_TYPE.SECONDARY)"
        (click)="markInsuranceAs('secondary', 'active')"
      >
        <mat-icon class="green">verified_user</mat-icon>
        <span>{{ 'eligibility.icon.active' | translate }}</span>
      </button>
    </div>
    <div
      [matTooltip]="'appointment.eligibility-status-text.manual-verification-unavailable' | translate"
      [matTooltipDisabled]="canMarkManually(PATIENT_INSURANCE_TYPE.SECONDARY) || appointment.eligibilitySecondaryStatus === ELIGIBILITY_STATUS.INACTIVE"
      matTooltipPosition="above"
    >
      <button
        mat-menu-item
        [disabled]="appointment.eligibilitySecondaryStatus === ELIGIBILITY_STATUS.INACTIVE || !canMarkManually(PATIENT_INSURANCE_TYPE.SECONDARY)"
        (click)="markInsuranceAs('secondary', 'inactive')"
      >
        <mat-icon class="yellow">remove_moderator</mat-icon>
        <span>{{ 'eligibility.icon.inactive' | translate }}</span>
      </button>
    </div>
  </mat-menu>

  <ng-template #workflowTemplate>
    <div fxLayout="column" fxLayoutGap="4px">
      <div
        fxLayout="row"
        fxLayoutAlign="start center"
        fxLayoutGap="8px"
        [matTooltip]="getEligibilityShieldTooltip(PATIENT_INSURANCE_TYPE.PRIMARY) | translate"
        matTooltipPosition="above"
      >
        <ng-container *ngIf="canMarkInsurance(PATIENT_INSURANCE_TYPE.PRIMARY); else primaryShield">
          <button
            *appHasPermission="'appointment.edit'; else primaryShield"
            mat-icon-button
            class="shield-trigger"
            [matMenuTriggerFor]="eligibilityMenu"
            [disabled]="inProgress"
            [attr.aria-label]="'eligibility.icon.mark-primary' | translate"
            (click)="$event.stopPropagation()"
          >
            <mat-progress-spinner
              *ngIf="isMarking(PATIENT_INSURANCE_TYPE.PRIMARY); else primaryShield"
              color="primary"
              mode="indeterminate"
              diameter="25"
            ></mat-progress-spinner>
          </button>
        </ng-container>
        <ng-template #primaryShield>
          <mat-icon
            [ngClass]="eligibilityStatusColor(appointment.eligibilityPrimaryStatus)"
            fxFlex="25px"
          >
            {{ eligibilityStatusIcon(appointment.eligibilityPrimaryStatus) }}
          </mat-icon>
        </ng-template>
        <span>
          {{ 'common.primary' | translate }}:
          {{ getEligibilityShieldText(PATIENT_INSURANCE_TYPE.PRIMARY) | translate }}
        </span>
      </div>
      <div
        *ngIf="showSecondaryShield()"
        fxLayout="row"
        fxLayoutAlign="start center"
        fxLayoutGap="8px"
        class="mt-3"
        [matTooltip]="getEligibilityShieldTooltip(PATIENT_INSURANCE_TYPE.SECONDARY) | translate"
        matTooltipPosition="above"
      >
        <ng-container *ngIf="canMarkInsurance(PATIENT_INSURANCE_TYPE.SECONDARY); else secondaryShield">
          <button
            *appHasPermission="'appointment.edit'; else secondaryShield"
            mat-icon-button
            class="shield-trigger"
            [matMenuTriggerFor]="secondaryInsuranceMenu"
            [disabled]="inProgress"
            [attr.aria-label]="'eligibility.icon.mark-secondary' | translate"
            (click)="$event.stopPropagation()"
          >
            <mat-progress-spinner
              *ngIf="isMarking(PATIENT_INSURANCE_TYPE.SECONDARY); else secondaryShield"
              color="primary"
              mode="indeterminate"
              diameter="25"
            ></mat-progress-spinner>
          </button>
        </ng-container>
        <ng-template #secondaryShield>
          <mat-icon
            [ngClass]="eligibilityStatusColor(appointment.eligibilitySecondaryStatus)"
            fxFlex="25px"
          >
            {{ eligibilityStatusIcon(appointment.eligibilitySecondaryStatus) }}
          </mat-icon>
        </ng-template>
        <span>
          {{ 'common.secondary' | translate }}:
          {{ getEligibilityShieldText(PATIENT_INSURANCE_TYPE.SECONDARY) | translate }}
        </span>
      </div>
    </div>
  </ng-template>
</ng-container>

<button *ngIf="status" mat-stroked-button>
  <mat-icon [ngClass]="eligibilityStatusColor(this.status)">
    <ng-container *ngIf="status === ELIGIBILITY_STATUS.ACTIVE">
      gpp_good
    </ng-container>
    <ng-container *ngIf="status === ELIGIBILITY_STATUS.INACTIVE">
      gpp_bad
    </ng-container>
    <ng-container *ngIf="status !== ELIGIBILITY_STATUS.ACTIVE && status !== ELIGIBILITY_STATUS.INACTIVE">
      shield
    </ng-container>
  </mat-icon>
  {{ eligibilityDisplayStatus(status) | translate }}
</button>
